import React, { Dispatch, ImgHTMLAttributes, Ref, SetStateAction, useState } from 'react'
import { cn } from '../utils/addToClassName'
import { Require } from '../utils/require'
import { StrictOmit } from '../utils/strictOmit'

/**
 * The image component is a wrapper around the HTML image that adds the
 * functionality of adding image crops using the sizes and srcSet props.
 */

export type ImgProps = Require<StrictOmit<ImgHTMLAttributes<HTMLImageElement>, 'srcSet'>, 'src'> & {
  crops?: ImageCrop[]
  fetchpriority?: 'high' | 'low' | 'auto' // can be removed when fetchpriority is supported in React
  imgRef?: Ref<HTMLImageElement> // in React 19, can be called just 'ref'
  loadedAndSetLoaded?: [boolean, Dispatch<SetStateAction<boolean>>]
}
export type ImageCrop = { width: number; url: string }

export const Img = ({ loadedAndSetLoaded, ...props }: ImgProps) =>
  loadedAndSetLoaded ? (
    <ImgWithoutLocalLoadedState {...props} loadedAndSetLoaded={loadedAndSetLoaded} />
  ) : (
    <ImgWithLocalLoadedState {...props} />
  )

const ImgWithLocalLoadedState = (props: ImgProps) => {
  const loadedAndSetLoaded = useState(false)
  return <ImgWithoutLocalLoadedState {...props} loadedAndSetLoaded={loadedAndSetLoaded} />
}

const ImgWithoutLocalLoadedState = ({
  crops,
  src,
  alt,
  sizes,
  className,
  onLoad,
  imgRef,
  loadedAndSetLoaded: [loaded, setLoaded],
  ...props
}: ImgProps & { loadedAndSetLoaded: [boolean, Dispatch<SetStateAction<boolean>>] }) => (
  <img
    {...props}
    ref={imgRef}
    loading={props.loading}
    alt={alt || ''}
    className={cn(loaded ? 'loaded' : 'loading', className)}
    src={crops?.[0] == undefined ? src : crops[0].url}
    srcSet={crops && crops.length > 0 ? getSrcSet(crops) : undefined}
    sizes={crops && crops.length > 0 ? sizes || DEFAULT_IMG_SIZES : undefined}
    onLoad={(e): void => {
      setLoaded(true)
      if (onLoad) onLoad(e)
    }}
  />
)

export const getSrcSet = (crops: ImageCrop[]) => crops.map(({ url, width }) => `${url} ${width}w`).join(', ')

export const DEFAULT_IMG_SIZES = '100vw'
